import { useRef, useEffect, useState } from 'react'
import ExpandableSection from '@cloudscape-design/components/expandable-section'
import Box from '@cloudscape-design/components/box'
import Spinner from '@cloudscape-design/components/spinner'
import Button from '@cloudscape-design/components/button'
import SpaceBetween from '@cloudscape-design/components/space-between'
import { useGenerateStore } from '@/state/generateStore'
import styles from './LiveStreamPanel.module.css'

export default function LiveStreamPanel() {
  const streamBuffer = useGenerateStore((s) => s.streamBuffer)
  const agentStatus = useGenerateStore((s) => s.agentStatus)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isAutoScrolling, setIsAutoScrolling] = useState(true)

  // DOM imperative: scroll to bottom when new tokens arrive
  useEffect(() => {
    if (!isAutoScrolling || !scrollRef.current) return
    const el = scrollRef.current
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) {
      el.scrollTop = el.scrollHeight
    } else {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }
  }, [streamBuffer, isAutoScrolling])

  const handleScroll = () => {
    if (!scrollRef.current) return
    const el = scrollRef.current
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20
    setIsAutoScrolling(isAtBottom)
  }

  if (!streamBuffer) {
    if (agentStatus === 'error') {
      return (
        <Box textAlign="center" padding="xl">
          <Box color="text-status-error" variant="small">
            Generation failed before any output was produced.
          </Box>
        </Box>
      )
    }
    return (
      <Box textAlign="center" padding="xl">
        <SpaceBetween size="s" alignItems="center">
          <Spinner size="normal" />
          <Box color="text-body-secondary" variant="small">
            Connecting to generation stream…
          </Box>
        </SpaceBetween>
      </Box>
    )
  }

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText="Live output"
      headerActions={<Spinner size="normal" />}
    >
      {/* Plain div justified: Cloudscape Box does not forward refs or expose onScroll */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={styles.scrollContainer}
        aria-live="polite"
        aria-atomic="false"
      >
        <Box fontSize="body-s" color="text-body-secondary" className={styles.streamText}>
          {streamBuffer}
        </Box>
      </div>
      {!isAutoScrolling && (
        <Box textAlign="center" padding="xs">
          <Button
            variant="link"
            onClick={() => setIsAutoScrolling(true)}
          >
            Resume scrolling ↓
          </Button>
        </Box>
      )}
    </ExpandableSection>
  )
}
